import { DEFAULT_AGENCY_PAGE_LIST_SIZE, fetchAgencies } from '@/api/customerMetaApi';
import { useGeoStore } from '@/stores/geo';
import { LoadState } from '@/stores/types';
import { ListingType, SortingOption, type Agency } from '@/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, Locale } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';

export interface AgencyProfilesState {
  agencies: Agency[];
  agenciesLoadState: LoadState;
  totalAgenciesCount: number;
  agencyListCurrentPage: number;
  selectedLocations: GeoLocation[];
  searchedLocations: GeoLocation[] | undefined;
  agenciesCountLoadState: LoadState;
  totalFilteredAgenciesCount: number;
  includeChildrenAgencies: boolean;
  selectedListingType: ListingType;
  selectedSortingOption: SortingOption;
  agenciesRequestGeneration: number;
}

const getSortByOption = (sortOption: SortingOption, listingType: ListingType) => {
  // Fallback for invalid sorting options when listing type is changed
  if (
    (listingType === ListingType.SALE && sortOption === SortingOption.PROPERTIES_FOR_RENT) ||
    (listingType === ListingType.RENT && sortOption === SortingOption.PROPERTIES_FOR_SALE)
  ) {
    return SortingOption.DEFAULT;
  }

  return sortOption;
};

const getLocationGeoId = (location: GeoLocation) => {
  return location.newId;
};

const extractParentLocationIds = (locations: GeoLocation[]): string[] => {
  const parentLocationIds: string[] = [];
  locations.forEach((location) => {
    if (location.parentPaths && location.parentPaths.length > 0) {
      const parentPath = location.parentPaths[0];
      const parentIds = parentPath.filter(
        (id: string) => !id.startsWith('geo-zipcode') && !id.startsWith('geo-country'),
      );
      parentLocationIds.push(...parentIds);
    }
  });
  return parentLocationIds;
};

export const useAgencyProfilesStore = defineStore('agencyProfiles', {
  state: (): AgencyProfilesState => ({
    agencies: [],
    agenciesLoadState: LoadState.UNDEFINED,
    totalAgenciesCount: 0,
    agencyListCurrentPage: 1,
    /* selectedLocations represent location autocomplete input state */
    selectedLocations: [],
    /* searchedLocations represent locations user actively searched for (clicked search button) */
    searchedLocations: undefined,
    agenciesCountLoadState: LoadState.UNDEFINED,
    totalFilteredAgenciesCount: 0,
    includeChildrenAgencies: false,
    selectedListingType: ListingType.ANY,
    selectedSortingOption: SortingOption.DEFAULT,
    agenciesRequestGeneration: 0,
  }),
  actions: {
    async resolveGeoLocations(
      geoLocations: string[],
      locale: HgSupportedLanguage,
    ): Promise<GeoLocation[]> {
      const geoStore = useGeoStore();
      const locationIds = geoLocations.filter((loc) => loc.includes('geo-'));
      const urlNames = geoLocations.filter((loc) => !loc.includes('geo-'));

      // Resolve locations by ID using geo store
      if (locationIds.length > 0) {
        await geoStore.resolveLocationsById(locationIds);
      }

      // Resolve locations by URL name using geo store
      if (urlNames.length > 0) {
        await geoStore.resolveLocationsByUrlName(urlNames, locale);
      }

      // Get resolved locations from geo store in the same order as input
      const resolvedLocations: GeoLocation[] = geoLocations
        .map((loc) => {
          if (loc.includes('geo-')) {
            return geoStore.getGeoLocationById(loc);
          } else {
            return geoStore.getGeoLocationByUrlName(loc, locale);
          }
        })
        .filter((loc): loc is GeoLocation => loc !== undefined);

      // Also resolve parent locations for breadcrumbs
      const parentLocationIds = extractParentLocationIds(resolvedLocations);
      if (parentLocationIds.length > 0) {
        await geoStore.resolveLocationsById(parentLocationIds);
      }

      return resolvedLocations;
    },
    /**
     * Loads the agencies for the current page.
     * @param currentPage - The current page number (1-based index)
     */
    async loadAgencies(currentPage = 1, requestGeneration?: number) {
      const generation = requestGeneration ?? ++this.agenciesRequestGeneration;

      this.agencies = [];
      this.agenciesLoadState = LoadState.LOADING;
      const geoLocationIds = this.selectedLocations
        .map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id);

      try {
        const agencies = await fetchAgencies({
          from: (currentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE,
          geoLocationIds: geoLocationIds,
          listingType: this.selectedListingType,
          includeChildren: this.includeChildrenAgencies,
          sortBy: this.selectedSortingOption,
        });

        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        this.agencies = agencies.records;
        this.totalAgenciesCount = agencies.total;
        this.totalFilteredAgenciesCount = agencies.total;
        this.agenciesLoadState = LoadState.LOADED;
      } catch (error) {
        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        this.agenciesLoadState = LoadState.FAILED;
      }
    },
    async loadAgenciesCount() {
      this.agenciesCountLoadState = LoadState.LOADING;
      const geoLocationIds = this.selectedLocations
        .map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id);

      try {
        const agencies = await fetchAgencies({
          size: 0,
          geoLocationIds: geoLocationIds,
          listingType: this.selectedListingType,
          includeChildren: this.includeChildrenAgencies,
        });
        this.totalFilteredAgenciesCount = agencies.total;
        this.agenciesCountLoadState = LoadState.LOADED;
      } catch (error) {
        this.agenciesCountLoadState = LoadState.FAILED;
      }
    },
    changeSelectedLocations(geoLocations: GeoLocation[]) {
      this.selectedLocations = geoLocations;
    },
    async changeUrlParams({
      geoLocations,
      locale,
      includeChildren,
      currentPage,
      listingType,
      sortBy,
    }: {
      geoLocations: string[];
      locale: HgSupportedLanguage;
      includeChildren: boolean;
      currentPage: number;
      listingType?: ListingType;
      sortBy?: SortingOption;
    }) {
      const generation = ++this.agenciesRequestGeneration;

      try {
        const resolvedLocations = await this.resolveGeoLocations(geoLocations, locale);

        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        if (resolvedLocations.length > 0) {
          this.$patch({
            selectedLocations: resolvedLocations,
          });
        }

        this.includeChildrenAgencies = includeChildren;

        if (listingType) {
          this.selectedListingType = listingType;
        }

        if (sortBy) {
          this.selectedSortingOption = getSortByOption(sortBy, this.selectedListingType);
        }

        await this.loadAgencies(currentPage, generation);

        if (generation !== this.agenciesRequestGeneration) {
          return;
        }

        // Set the state later to trigger the FeaturedPlacement component changed after the agencies are loaded
        this.$patch({
          searchedLocations: resolvedLocations ?? this.selectedLocations,
          agencyListCurrentPage: currentPage,
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
  getters: {
    pageCount: (state): number =>
      Math.ceil(state.totalAgenciesCount / DEFAULT_AGENCY_PAGE_LIST_SIZE),
    nextPageRoute: (state) => {
      return (page: number, locale: Locale) => {
        const nextRoute: {
          params: { location: string | undefined };
          query: {
            page?: number;
            loc?: string;
            includeChildren?: boolean;
            listingType?: ListingType;
            sortBy?: SortingOption;
          };
        } = {
          params: { location: undefined },
          query: {
            ...(page > 1 ? { page } : {}),
            ...(state.includeChildrenAgencies
              ? { includeChildren: state.includeChildrenAgencies }
              : {}),
            ...(state.selectedListingType !== ListingType.ANY
              ? { listingType: state.selectedListingType }
              : {}),
            ...(getSortByOption(state.selectedSortingOption, state.selectedListingType) !==
            SortingOption.DEFAULT
              ? { sortBy: state.selectedSortingOption }
              : {}),
          },
        };
        const selectedLocations = state.selectedLocations;

        if (selectedLocations.length > 0) {
          nextRoute.params = { location: selectedLocations[0].urlNames[locale] };
        }

        if (selectedLocations.length > 1) {
          nextRoute.query.loc = selectedLocations
            .slice(1)
            .map((location) => getLocationGeoId(location) ?? '')
            .filter((id) => !!id)
            .join(',');
        }

        if (selectedLocations.length === 0) {
          nextRoute.params = { location: '' };
        }

        return nextRoute;
      };
    },
    searchedGeoIds: (state) =>
      state.searchedLocations
        ?.map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id),
  },
});
